import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useAuth } from '../context/AuthContext';
import { scratchCards } from '../services/api';

const LoyaltyProgramPage = () => {
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (user) {
      fetchRedemptionHistory();
    }
  }, [user]);

  const fetchRedemptionHistory = async () => {
    try {
      const response = await scratchCards.getUserHistory();
      setHistory(response.data.redemptionHistory);
    } catch (err) {
      setError('Failed to fetch redemption history');
    }
  };

  const handleRedeem = async () => {
    if (!code) {
      setError('Please enter a scratch card code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await scratchCards.redeem({ code });
      setSuccess(`Successfully redeemed! You earned ${response.data.pointsEarned} points`);
      setCode('');
      fetchRedemptionHistory();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to redeem code');
    } finally {
      setLoading(false);
    }
  };

  const handleScanQR = () => {
    // TODO: Implement QR code scanning
    alert('QR scanning feature coming soon!');
  };

  if (!user) {
    return (
      <Container maxWidth="sm">
        <Alert severity="info">
          Please login to access the loyalty program.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Loyalty Program
        </Typography>
      </Box>

      {/* Points Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs>
              <Typography color="textSecondary" gutterBottom>
                Your Points Balance
              </Typography>
              <Typography variant="h3">
                {user.loyaltyPoints || 0}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Redeem Card */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Redeem Scratch Card
        </Typography>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Enter Scratch Card Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            margin="normal"
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleRedeem}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Redeem'}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<QrCodeScannerIcon />}
              onClick={handleScanQR}
            >
              Scan QR
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
      </Paper>

      {/* Redemption History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Redemption History
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Code</TableCell>
                <TableCell align="right">Points</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((item) => (
                <TableRow key={item._id}>
                  <TableCell>
                    {new Date(item.redeemedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{item.code}</TableCell>
                  <TableCell align="right">{item.points}</TableCell>
                </TableRow>
              ))}
              {history.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No redemption history yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default LoyaltyProgramPage;